import { useTranslation } from 'react-i18next';
import { ConfigProvider, Tour } from 'antd';
import { TOUR_TARGETS } from './profileGuideConfig';

// Text keys under profileGuide.*; a `{kind}` text differs per profile.
const STEP_TEXT = {
  header: { title: 'headerTitle', text: 'headerText{kind}', placement: 'bottom' },
  actions: { title: 'actionsTitle', text: 'actionsText{kind}', placement: 'bottomRight' },
  tiles: { title: 'tilesTitle', text: 'tilesText{kind}', placement: 'right' },
  alert: { title: 'alertTitle', text: 'alertText', placement: 'right' },
  middle: { title: 'middleTitle', text: 'middleText{kind}', placement: 'right' },
  collapse: { title: 'collapseTitle', text: 'collapseText', placement: 'left' },
  panel: { title: 'panelTitle', text: 'panelText', placement: 'left' },
  edit: { title: 'editTitle', text: 'editText', placement: 'bottomRight' },
  save: { title: 'saveTitle', text: 'saveText', placement: 'topRight' },
  dirty: { title: 'dirtyTitle', text: 'dirtyText', placement: 'right' },
  help: { title: 'helpTitle', text: 'helpText', placement: 'bottomRight' },
  contact: { title: 'contactTitle', text: 'contactText', placement: 'right' },
};

// Round buttons, like the rest of the platform.
const ROUND = {
  components: { Button: { borderRadiusSM: 50, borderRadius: 50 } },
};

/**
 * The profile guide itself (antd Tour). `steps` are the step ids that are on
 * the page; null keeps it closed.
 */
export default function ProfileTour({ kind, steps, current, onChange, onClose }) {
  const { t } = useTranslation('translations');
  const tg = (key, opts) =>
    t(
      `profileGuide.${key.replace('{kind}', kind === 'company' ? 'Company' : 'Employee')}`,
      opts
    );

  if (!steps) return null;

  const tourSteps = steps.map((id, idx) => {
    const last = idx === steps.length - 1;
    // Save is on the page only once the panel is in edit mode; until then
    // the panel stands in for it.
    const target =
      id === 'save'
        ? () =>
            document.querySelector(TOUR_TARGETS.save) ||
            document.querySelector(TOUR_TARGETS.panel)
        : () => document.querySelector(TOUR_TARGETS[id]);
    // The contact link is "Contact us" for most and "Support tickets" for
    // super admins: name the one this person has.
    const place =
      id === 'contact'
        ? document
            .querySelector(`${TOUR_TARGETS.contact} span`)
            ?.textContent?.trim()
        : undefined;
    return {
      title: tg(STEP_TEXT[id].title),
      description: tg(STEP_TEXT[id].text, { place }),
      placement: STEP_TEXT[id].placement,
      target,
      nextButtonProps: { children: last ? tg('finish') : tg('next') },
      prevButtonProps: { children: tg('prev') },
    };
  });

  return (
    <ConfigProvider theme={ROUND}>
      <Tour
        open
        steps={tourSteps}
        current={current}
        onChange={onChange}
        onClose={onClose}
        onFinish={onClose}
        zIndex={1100}
        scrollIntoViewOptions={false}
      />
    </ConfigProvider>
  );
}
